import React, { FC, useEffect, useRef, useState } from "react";
import styles from "./Quiz.module.scss";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import StaveRenderer, { DerivedNote } from "./StaveRenderer";
import { playRhythm, stopRhythm, decodeRhythm } from "./rhythmAudio";

// ─── JSON types ──────────────────────────────────────────────────────────────

interface DerivedConfig {
  /** Single clef string or array to pick from: "treble" | "bass" | "alto" | "tenor" */
  clef?: string | string[];
  /** Single time signature or array to pick from, e.g. ["4/4", "3/4"] */
  time_signature?: string | string[];
  /** Single key signature or array to pick from, e.g. ["C", "G", "F"] */
  key_signature?: string | string[];
  /** Pitch range as [startPitch, endPitch], e.g. ["C4", "G5"]. Replaces pitches + octave_range. */
  pitch_range?: [string, string];
  /** Pitch classes to include within pitch_range. Defaults to natural notes when omitted. */
  pitch_classes?: string[];
  /** Pool of pitches to pick from (legacy — use pitch_range instead) */
  pitches?: string[];
  /** Pool of major key signatures (for key-signature subtype) */
  key_signatures?: string[];
  /** [min, max] octave, inclusive (legacy — use pitch_range instead) */
  octave_range?: [number, number];
  /** How many independent questions to generate from this template */
  question_count: number;
  /**
   * Ordered list of note values — one entry per note rendered on the stave.
   * Length implicitly sets how many notes appear.
   *   4=semibreve  2=minim  3=dotted minim  1=crotchet
   *   1.5=dotted crotchet  0.5=quaver  0.75=dotted quaver  0.25=semiquaver
   */
  note_value?: number[];
}

/** A single note in an explicit rhythm sequence */
interface SequenceNote {
  pitch: string;   // e.g. "B", "F#"
  octave: number;  // e.g. 4
  note_value: number; // 4=semibreve 2=minim 1=crotchet 0.5=quaver … (dotted uses 3, 1.5, 0.75)
}

interface SequenceConfig {
  clef?: string;
  time_signature?: string;
  key_signature?: string;
  notes: SequenceNote[];
  /** Pairs of note indices [from, to] connected by a tie */
  ties?: Array<[number, number]>;
  /** Pairs of note indices [from, to] connected by a slur */
  slurs?: Array<[number, number]>;
}

/** Constraints from which a rhythm sequence is auto-generated. */
interface GeneratorConfig {
  /** Number of bars to generate */
  bars: number;
  /** How many independent questions to generate from this template (default 1) */
  question_count?: number;
  /** Allowed clefs to pick from */
  clef?: string | string[];
  /** Allowed key signatures to pick from */
  key_signature?: string | string[];
  /** Allowed time signatures to pick from, e.g. ["4/4", "3/4"] */
  time_signature?: string | string[];
  /** Allowed note values (crotchet-beats): 4,3,2,1.5,1,0.75,0.5,0.375,0.25 */
  note_values: number[];
  /** Pitch pool for the notes (rhythm is usually single-pitch). Defaults per clef. */
  pitches?: string[];
  /** Whether ties may be generated */
  allow_ties?: boolean;
  /** Whether slurs may be generated */
  allow_slurs?: boolean;
  /** Probability [0..1] of a tie at each eligible adjacent pair (default 0.25) */
  tie_probability?: number;
  /** Probability [0..1] of a slur at each eligible adjacent pair (default 0.2) */
  slur_probability?: number;
  /** Playback tempo in BPM (default 100) */
  tempo?: number;
}

interface Question {
  id: number;
  question: string;
  type: "direct" | "derived" | "sequence";
  subtype?: string;
  options?: string[];
  correct?: string;
  derived_config?: DerivedConfig;
  sequence_config?: SequenceConfig;
  generator_config?: GeneratorConfig;
  option_count?: number;
  /** Pre-assigned pitches distributed without repetition across instances */
  _instance_pitches?: string[];
}

interface DifficultyLevel {
  level: string;
  questions: Question[];
}

interface QuizData {
  title: string;
  difficulty_levels: DifficultyLevel[];
}

// ─── Runtime state for a derived question ────────────────────────────────────

interface GeneratedDerived {
  notes: DerivedNote[];
  correct: string;
  options: string[];
  clef: string;
  timeSignature?: string;
  keySignature?: string;
  ties?: Array<[number, number]>;
  slurs?: Array<[number, number]>;
  barBreaks?: number[];
  tempo?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function pickOne(val: string | string[] | undefined, fallback: string): string {
  if (!val) return fallback;
  if (Array.isArray(val)) return val[Math.floor(Math.random() * val.length)];
  return val;
}

// ─── Pitch-range helpers ──────────────────────────────────────────────────────

const FLAT_TO_SHARP: Record<string, string> = {
  Bb: "A#", Eb: "D#", Ab: "G#", Db: "C#", Gb: "F#", Cb: "B", Fb: "E",
};
const CHROMATIC_SEMITONE: Record<string, number> = {
  C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5,
  "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11,
};
const DEFAULT_PITCH_CLASSES = ["C", "D", "E", "F", "G", "A", "B"];

function parsePitchStr(s: string): { name: string; octave: number } {
  const m = s.match(/^([A-G][#b]?)(\d+)$/);
  if (!m) return { name: "C", octave: 4 };
  return { name: m[1], octave: parseInt(m[2], 10) };
}

function pitchToAbsolute(name: string, octave: number): number {
  const canonical = FLAT_TO_SHARP[name] ?? name;
  return octave * 12 + (CHROMATIC_SEMITONE[canonical] ?? 0);
}

function expandPitchRange(
  startStr: string,
  endStr: string,
  pitchClasses?: string[]
): Array<{ name: string; octave: number }> {
  const start = parsePitchStr(startStr);
  const end   = parsePitchStr(endStr);
  const lo = pitchToAbsolute(start.name, start.octave);
  const hi = pitchToAbsolute(end.name, end.octave);
  const classes = pitchClasses ?? DEFAULT_PITCH_CLASSES;
  const result: Array<{ name: string; octave: number }> = [];
  for (let oct = start.octave; oct <= end.octave; oct++) {
    for (const cls of classes) {
      const abs = pitchToAbsolute(cls, oct);
      if (abs >= lo && abs <= hi) result.push({ name: cls, octave: oct });
    }
  }
  return result;
}

const RELATIVE_MAP: Record<string, string> = {
  C: "Am", G: "Em", D: "Bm", A: "F#m", E: "C#m", B: "G#m",
  "F#": "D#m", "C#": "A#m",
  F: "Dm", Bb: "Gm", Eb: "Cm", Ab: "Fm", Db: "Bbm", Gb: "Ebm", Cb: "Abm",
  Am: "C", Em: "G", Bm: "D", "F#m": "A", "C#m": "E", "G#m": "B",
  "D#m": "F#", "A#m": "C#",
  Dm: "F", Gm: "Bb", Cm: "Eb", Fm: "Ab", Bbm: "Db", Ebm: "Gb", Abm: "Cb",
};

function noteValueToVex(value: number): { duration: string; dotted: boolean } {
  switch (value) {
    case 4:     return { duration: "w",  dotted: false };
    case 3:     return { duration: "h",  dotted: true  };
    case 2:     return { duration: "h",  dotted: false };
    case 1.5:   return { duration: "q",  dotted: true  };
    case 1:     return { duration: "q",  dotted: false };
    case 0.75:  return { duration: "8",  dotted: true  };
    case 0.5:   return { duration: "8",  dotted: false };
    case 0.375: return { duration: "16", dotted: true  };
    case 0.25:  return { duration: "16", dotted: false };
    default:    return { duration: "q",  dotted: false };
  }
}

function buildOptions(
  correct: string,
  pitches: string[],
  count: number,
  optionCount: number
): string[] {
  const set = new Set<string>([correct]);
  let attempts = 0;
  while (set.size < Math.min(optionCount, pitches.length) && attempts < 200) {
    const distractor = Array.from({ length: count }, () =>
      pitches[Math.floor(Math.random() * pitches.length)]
    ).join(", ");
    if (distractor !== correct) set.add(distractor);
    attempts++;
  }
  return shuffle(Array.from(set));
}

function expandQuestions(raw: Question[]): Question[] {
  const out: Question[] = [];
  raw.forEach((q) => {
    if (q.type === "sequence" && q.generator_config) {
      const n = q.generator_config.question_count ?? 1;
      for (let i = 0; i < n; i++) out.push({ ...q, id: q.id * 1000 + i });
      return;
    }
    if (q.type === "derived" && q.derived_config) {
      const cfg = q.derived_config;
      const n = cfg.question_count ?? 1;
      // key-signature doesn't need pitch pre-assignment
      if (q.subtype === "key-signature") {
        for (let i = 0; i < n; i++) {
          out.push({ ...q, id: q.id * 1000 + i });
        }
        return;
      }
      const count = cfg.note_value?.length ?? 1;
      const totalNeeded = n * count;
      const supply: string[] = [];

      if (cfg.pitch_range) {
        const pool = expandPitchRange(cfg.pitch_range[0], cfg.pitch_range[1], cfg.pitch_classes);
        // Store as "C4", "F#5" etc. so octave is captured
        while (supply.length < totalNeeded)
          supply.push(...shuffle(pool.map(({ name, octave }) => `${name}${octave}`)));
      } else {
        while (supply.length < totalNeeded) supply.push(...shuffle([...cfg.pitches!]));
      }

      for (let i = 0; i < n; i++) {
        out.push({
          ...q,
          id: q.id * 1000 + i,
          _instance_pitches: supply.slice(i * count, (i + 1) * count),
        });
      }
    } else {
      out.push(q);
    }
  });
  return out;
}

function generateDerived(q: Question): GeneratedDerived {
  const cfg = q.derived_config!;
  const { note_value } = cfg;
  const count = note_value?.length ?? 1;

  const clef = pickOne(cfg.clef, "treble");
  const timeSignature = cfg.time_signature ? pickOne(cfg.time_signature, "") : undefined;
  const keySignature  = cfg.key_signature  ? pickOne(cfg.key_signature, "C")  : undefined;

  // ── Key-signature subtype ──
  if (q.subtype === "key-signature" && cfg.key_signatures && cfg.key_signatures.length > 0) {
    const ksPool = cfg.key_signatures;
    const raw = ksPool[Math.floor(Math.random() * ksPool.length)];
    const isMinor = raw.length > 1 && raw.endsWith("m");
    const label = isMinor ? raw.slice(0, -1) + " minor" : raw + " major";
    const relative = RELATIVE_MAP[raw];
    const relativeLabel = relative
      ? (relative.length > 1 && relative.endsWith("m")
          ? relative.slice(0, -1) + " minor"
          : relative + " major")
      : null;
    const allLabels = ksPool.map((k) =>
      k.length > 1 && k.endsWith("m") ? k.slice(0, -1) + " minor" : k + " major"
    ).filter((l) => l !== relativeLabel);
    const options = buildOptions(label, allLabels, 1, q.option_count ?? 4);
    return { notes: [], correct: label, options, clef, timeSignature, keySignature: raw };
  }

  // ── pitch_range path ──
  if (cfg.pitch_range) {
    const pool = expandPitchRange(cfg.pitch_range[0], cfg.pitch_range[1], cfg.pitch_classes);
    const pitchClassPool = Array.from(new Set(pool.map(({ name }) => name)));

    // _instance_pitches stored as "C4", "F#5" etc.
    const assignedNotes = (q._instance_pitches ?? pool.slice(0, count).map(({ name, octave }) => `${name}${octave}`))
      .map((s) => {
        const m = s.match(/^([A-G][#b]?)(\d+)$/);
        return m ? { name: m[1], octave: parseInt(m[2], 10) } : { name: "C", octave: 4 };
      });

    const notes: DerivedNote[] = assignedNotes.map(({ name, octave }, i) => {
      const { duration, dotted } = noteValueToVex(note_value![i] ?? 1);
      return { pitch: name, octave, duration, dotted };
    });

    const correct = notes.map((n) => n.pitch).join(", ");
    const options = buildOptions(correct, pitchClassPool, count, q.option_count ?? 4);
    return { notes, correct, options, clef, timeSignature, keySignature };
  }

  // ── Legacy path: pitches + octave_range ──
  const { pitches, octave_range } = cfg;
  const assignedPitches: string[] =
    q._instance_pitches ??
    Array.from({ length: count }, () =>
      pitches![Math.floor(Math.random() * pitches!.length)]
    );

  const notes: DerivedNote[] = assignedPitches.map((pitch, i) => {
    const { duration, dotted } = noteValueToVex(note_value![i]);
    return { pitch, octave: randomInt(octave_range![0], octave_range![1]), duration, dotted };
  });

  const correct = notes.map((n) => n.pitch).join(", ");
  const options = buildOptions(correct, pitches!, count, q.option_count ?? 4);
  return { notes, correct, options, clef, timeSignature, keySignature };
}

// ─── Rhythm helpers ───────────────────────────────────────────────────────────

function roundBeat(v: number): number {
  return Math.round(v * 1000) / 1000;
}

function encodeRhythm(onsets: number[]): string {
  return onsets.map(roundBeat).join(",");
}

/** Onset durations (beats) that are actually sounded — tied notes merge into one. */
function computeOnsets(notes: SequenceNote[], ties?: Array<[number, number]>): number[] {
  const tiedTargets = new Set((ties ?? []).map(([, to]) => to));
  const onsets: number[] = [];
  notes.forEach((n, i) => {
    if (tiedTargets.has(i) && onsets.length > 0) {
      onsets[onsets.length - 1] = roundBeat(onsets[onsets.length - 1] + n.note_value);
    } else {
      onsets.push(n.note_value);
    }
  });
  return onsets;
}

/** Crotchet-beats in one bar of the given time signature, e.g. "6/8" → 3. */
function timeSignatureBeats(ts: string): number {
  const [num, den] = ts.split("/").map(Number);
  if (!num || !den) return 4;
  return roundBeat((num * 4) / den);
}

/** Metronome count-in for one bar: one click per notated beat. */
function countInFor(ts?: string): { clicks: number; interval: number } {
  if (!ts) return { clicks: 4, interval: 1 };
  const [num, den] = ts.split("/").map(Number);
  if (!num || !den) return { clicks: 4, interval: 1 };
  return { clicks: num, interval: roundBeat(4 / den) };
}

/** Fill one bar of `beats` crotchet-beats using the allowed note values. */
function fillBar(beats: number, values: number[]): number[] {
  const usable = values.filter((v) => v > 0 && v <= beats + 1e-6);
  if (usable.length === 0) return [beats];
  const minVal = Math.min(...usable);
  for (let attempt = 0; attempt < 300; attempt++) {
    const out: number[] = [];
    let rem = beats;
    let ok = true;
    while (rem > 1e-6) {
      const r = rem;
      const candidates = usable.filter(
        (v) => v <= r + 1e-6 && (Math.abs(v - r) < 1e-6 || r - v >= minVal - 1e-6)
      );
      if (candidates.length === 0) { ok = false; break; }
      const v = candidates[Math.floor(Math.random() * candidates.length)];
      out.push(v);
      rem = roundBeat(rem - v);
    }
    if (ok && Math.abs(rem) < 1e-6 && out.length > 0) return out;
  }
  const count = Math.round(beats / minVal);
  if (Math.abs(count * minVal - beats) < 1e-6) return new Array(count).fill(minVal);
  return [beats];
}

const CLEF_MIDDLE_PITCH: Record<string, { pitch: string; octave: number }> = {
  treble: { pitch: "B", octave: 4 },
  bass:   { pitch: "D", octave: 3 },
  alto:   { pitch: "C", octave: 4 },
  tenor:  { pitch: "A", octave: 3 },
};

interface GeneratedSequence {
  clef: string;
  timeSignature: string;
  keySignature?: string;
  notes: SequenceNote[];
  ties: Array<[number, number]>;
  slurs: Array<[number, number]>;
  barBreaks: number[];
  tempo: number;
}

/** Auto-generate a rhythm sequence from constraints. */
function generateRhythmSequence(cfg: GeneratorConfig): GeneratedSequence {
  const clef = pickOne(cfg.clef, "treble");
  const timeSignature = pickOne(cfg.time_signature, "4/4");
  const keySignature = cfg.key_signature ? pickOne(cfg.key_signature, "C") : undefined;
  const beatsPerBar = timeSignatureBeats(timeSignature);
  const bars = Math.max(1, cfg.bars ?? 1);

  const mid = CLEF_MIDDLE_PITCH[clef] ?? { pitch: "B", octave: 4 };
  const pitchPool =
    cfg.pitches && cfg.pitches.length > 0 ? cfg.pitches : [`${mid.pitch}${mid.octave}`];

  const notes: SequenceNote[] = [];
  const barBreaks: number[] = [];

  for (let b = 0; b < bars; b++) {
    if (b > 0) barBreaks.push(notes.length);
    const barValues = fillBar(beatsPerBar, cfg.note_values);
    barValues.forEach((v) => {
      const parsed = parsePitchStr(pitchPool[Math.floor(Math.random() * pitchPool.length)]);
      notes.push({ pitch: parsed.name, octave: parsed.octave, note_value: v });
    });
  }

  // Ties — connect adjacent notes (forced to share pitch so the tie is valid)
  const ties: Array<[number, number]> = [];
  const tiedIdx = new Set<number>();
  if (cfg.allow_ties) {
    const p = cfg.tie_probability ?? 0.25;
    for (let i = 0; i < notes.length - 1; i++) {
      if (tiedIdx.has(i) || tiedIdx.has(i + 1)) continue;
      if (Math.random() < p) {
        notes[i + 1].pitch = notes[i].pitch;
        notes[i + 1].octave = notes[i].octave;
        ties.push([i, i + 1]);
        tiedIdx.add(i);
        tiedIdx.add(i + 1);
      }
    }
  }

  // Slurs — adjacent notes not already involved in a tie
  const slurs: Array<[number, number]> = [];
  if (cfg.allow_slurs) {
    const p = cfg.slur_probability ?? 0.2;
    for (let i = 0; i < notes.length - 1; i++) {
      if (tiedIdx.has(i) || tiedIdx.has(i + 1)) continue;
      if (Math.random() < p) {
        slurs.push([i, i + 1]);
        i++; // avoid immediately overlapping slurs
      }
    }
  }

  return { clef, timeSignature, keySignature, notes, ties, slurs, barBreaks, tempo: cfg.tempo ?? 100 };
}

/** Randomly fill `total` beats with common note values. */
function randomFill(total: number): number[] {
  const pool = [0.5, 1, 1.5, 2];
  const out: number[] = [];
  let rem = total;
  let guard = 0;
  while (rem > 0.001 && guard++ < 64) {
    const r = rem;
    const choices = pool.filter((v) => v <= r + 1e-6);
    if (choices.length === 0) {
      out.push(roundBeat(rem));
      break;
    }
    const v = choices[Math.floor(Math.random() * choices.length)];
    out.push(v);
    rem = roundBeat(rem - v);
  }
  return out;
}

/** Produce a rhythm audibly different from `onsets` but same total length. */
function mutateRhythm(onsets: number[]): number[] {
  const a = [...onsets];
  const strategy = Math.floor(Math.random() * 3);

  if (strategy === 0) {
    // split a note (>= 0.5) into two equal halves
    const idxs = a.map((v, i) => (v >= 0.5 ? i : -1)).filter((i) => i >= 0);
    if (idxs.length) {
      const i = idxs[Math.floor(Math.random() * idxs.length)];
      const half = roundBeat(a[i] / 2);
      a.splice(i, 1, half, half);
      return a;
    }
  } else if (strategy === 1 && a.length >= 2) {
    // merge two adjacent notes
    const i = Math.floor(Math.random() * (a.length - 1));
    a.splice(i, 2, roundBeat(a[i] + a[i + 1]));
    return a;
  } else if (a.length >= 2) {
    // swap two distinct adjacent notes
    for (let tries = 0; tries < 6; tries++) {
      const i = Math.floor(Math.random() * (a.length - 1));
      if (a[i] !== a[i + 1]) {
        [a[i], a[i + 1]] = [a[i + 1], a[i]];
        return a;
      }
    }
  }
  return randomFill(onsets.reduce((s, v) => s + v, 0));
}

function generateRhythmOptions(
  correct: number[],
  optionCount: number
): { options: string[]; correct: string } {
  const total = roundBeat(correct.reduce((s, v) => s + v, 0));
  const correctKey = encodeRhythm(correct);
  const seen = new Set<string>([correctKey]);
  const distractors: string[] = [];
  let attempts = 0;

  while (distractors.length < optionCount - 1 && attempts < 600) {
    attempts++;
    const cand = Math.random() < 0.65 ? mutateRhythm(correct) : randomFill(total);
    const sum = roundBeat(cand.reduce((s, v) => s + v, 0));
    if (Math.abs(sum - total) > 1e-6 || cand.some((v) => v <= 0)) continue;
    const key = encodeRhythm(cand);
    if (seen.has(key)) continue;
    seen.add(key);
    distractors.push(key);
  }

  return { options: shuffle([correctKey, ...distractors]), correct: correctKey };
}

/** Build a rhythm sequence with audio options — auto-generated or explicit. */
function generateSequence(q: Question): GeneratedDerived {
  let clef: string;
  let timeSignature: string | undefined;
  let keySignature: string | undefined;
  let seqNotes: SequenceNote[];
  let ties: Array<[number, number]> | undefined;
  let slurs: Array<[number, number]> | undefined;
  let barBreaks: number[] | undefined;
  let tempo: number | undefined;

  if (q.generator_config) {
    const g = generateRhythmSequence(q.generator_config);
    clef = g.clef;
    timeSignature = g.timeSignature;
    keySignature = g.keySignature;
    seqNotes = g.notes;
    ties = g.ties;
    slurs = g.slurs;
    barBreaks = g.barBreaks;
    tempo = g.tempo;
  } else {
    const cfg = q.sequence_config!;
    clef = cfg.clef ?? "treble";
    timeSignature = cfg.time_signature;
    keySignature = cfg.key_signature;
    seqNotes = cfg.notes;
    ties = cfg.ties;
    slurs = cfg.slurs;
    tempo = 100;
  }

  const notes: DerivedNote[] = seqNotes.map((n) => {
    const { duration, dotted } = noteValueToVex(n.note_value);
    return { pitch: n.pitch, octave: n.octave, duration, dotted };
  });

  const onsets = computeOnsets(seqNotes, ties);
  const { options, correct } = generateRhythmOptions(onsets, q.option_count ?? 4);

  return { notes, correct, options, clef, timeSignature, keySignature, ties, slurs, barBreaks, tempo };
}

function extractDifficulties(levels: DifficultyLevel[]): string[] {
  return levels.map((l) => l.level);
}

function questionCount(levels: DifficultyLevel[], difficulty: string): number {
  const level = levels.find((l) => l.level === difficulty);
  if (!level) return 0;
  return level.questions.reduce((sum, q) => {
    if (q.type === "derived" && q.derived_config) return sum + q.derived_config.question_count;
    if (q.type === "sequence" && q.generator_config) return sum + (q.generator_config.question_count ?? 1);
    return sum + 1;
  }, 0);
}

const DIFFICULTY_COLOUR_MAP: Record<string, string> = {
  easy:         "#2e7d32",
  beginner:     "#2e7d32",
  medium:       "#e65100",
  intermediate: "#e65100",
  hard:         "#b71c1c",
  advanced:     "#b71c1c",
  expert:       "#6a1b9a",
};
const FALLBACK_COLOURS = ["#195d7a", "#5c6bc0", "#00695c", "#6d4c41"];

function difficultyColour(label: string, index: number): string {
  return DIFFICULTY_COLOUR_MAP[label.toLowerCase()] ?? FALLBACK_COLOURS[index % FALLBACK_COLOURS.length];
}

/** Format seconds as MM:SS */
function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface QuizProps {
  subtileId: string;
  subtileLabel: string;
}

const Quiz: FC<QuizProps> = ({ subtileId, subtileLabel }) => {
  const [levels, setLevels]                 = useState<DifficultyLevel[]>([]);
  const [difficulties, setDifficulties]     = useState<string[]>([]);
  const [difficulty, setDifficulty]         = useState<string | null>(null);
  const [questions, setQuestions]           = useState<Question[]>([]);
  const [derivedMap, setDerivedMap]         = useState<Record<number, GeneratedDerived>>({});
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);

  const [currentIndex, setCurrentIndex]     = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [validated, setValidated]           = useState(false);
  const [score, setScore]                   = useState(0);
  const [finished, setFinished]             = useState(false);

  // ── Timer
  const intervalRef                         = useRef<ReturnType<typeof setInterval> | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const startTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setElapsedSeconds(0);
    intervalRef.current = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => () => { stopTimer(); stopRhythm(); }, []);

  // ── Load JSON
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/quiz-data/${subtileId}.json`)
      .then((res) => {
        if (!res.ok) throw new Error("Not found");
        return res.json() as Promise<QuizData>;
      })
      .then((data) => {
        const diffs = extractDifficulties(data.difficulty_levels);
        setLevels(data.difficulty_levels);
        setDifficulties(diffs);
        if (diffs.length === 0) {
          // No difficulty levels — start immediately
          setQuestions(shuffle(expandQuestions([])));
          startTimer();
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Could not load quiz. Please try again.");
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtileId]);

  // ── Pre-generate derived data once active questions are set
  useEffect(() => {
    if (questions.length === 0) return;
    const map: Record<number, GeneratedDerived> = {};
    questions.forEach((q) => {
      if (q.type === "derived" && q.derived_config) map[q.id] = generateDerived(q);
      else if (q.type === "sequence" && (q.generator_config || q.sequence_config)) map[q.id] = generateSequence(q);
    });
    setDerivedMap(map);
  }, [questions]);

  // ── Start quiz for the chosen difficulty
  const startWithDifficulty = (diff: string) => {
    const level = levels.find((l) => l.level === diff);
    const filtered = level?.questions ?? [];
    setQuestions(shuffle(expandQuestions(filtered)));
    setDifficulty(diff);
    setCurrentIndex(0);
    setSelectedOption(null);
    setValidated(false);
    setScore(0);
    setFinished(false);
    startTimer();
  };

  // ── Early returns
  if (loading) return <div className={styles.Quiz__loading}><CircularProgress /></div>;
  if (error)   return <div className={styles.Quiz__error}>{error}</div>;

  // ── Difficulty selection screen
  if (difficulties.length > 0 && difficulty === null) {
    return (
      <div className={styles.Quiz__difficulty}>
        <div className={styles.Quiz__difficulty_title}>Choose Difficulty</div>
        <div className={styles.Quiz__difficulty_cards}>
          {difficulties.map((diff, idx) => {
            const colour = difficultyColour(diff, idx);
            const count  = questionCount(levels, diff);
            return (
              <div
                key={diff}
                className={styles.Quiz__difficulty_card}
                style={{ borderColor: colour }}
                onClick={() => startWithDifficulty(diff)}
              >
                <div className={styles.Quiz__difficulty_card_label} style={{ color: colour }}>
                  {diff}
                </div>
                <div className={styles.Quiz__difficulty_card_desc}>
                  {count} question{count !== 1 ? "s" : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Quiz screen
  const currentQuestion = questions[currentIndex];
  const isLast = currentIndex === questions.length - 1;
  const usesStave = currentQuestion?.type === "derived" || currentQuestion?.type === "sequence";
  const derived = usesStave ? derivedMap[currentQuestion.id] : undefined;

  const options: string[] =
    usesStave ? derived?.options ?? [] : currentQuestion?.options ?? [];

  const correctAnswer: string =
    usesStave ? derived?.correct ?? "" : currentQuestion?.correct ?? "";

  const handleNext = () => {
    if (selectedOption === null) return;
    if (!validated) {
      const isCorrect = selectedOption === correctAnswer;
      setScore((s) => s + (isCorrect ? 1 : 0));
      setValidated(true);
      return;
    }
    if (isLast) {
      stopTimer();
      stopRhythm();
      setFinished(true);
    } else {
      stopRhythm();
      setCurrentIndex((i) => i + 1);
      setSelectedOption(null);
      setValidated(false);
    }
  };

  // ── Results screen
  if (finished) {
    return (
      <div className={styles.Quiz__results}>
        <div className={styles.Quiz__results_title}>Quiz Complete!</div>
        <div className={styles.Quiz__results_score}>
          You scored <strong>{score}</strong> out of <strong>{questions.length}</strong>
        </div>
        <div className={styles.Quiz__results_time}>
          Time taken: <strong>{formatTime(elapsedSeconds)}</strong>
        </div>
        <div className={styles.Quiz__results_subtitle}>
          {score === questions.length
            ? "Perfect score!"
            : score >= Math.ceil(questions.length / 2)
            ? "Good job!"
            : "Keep practicing!"}
        </div>
      </div>
    );
  }

  const staveReady = !usesStave || derived !== undefined;

  const optionClass = (opt: string): string => {
    const base = styles.Quiz__option;
    if (!validated) return selectedOption === opt ? `${base} ${styles.Quiz__option_selected}` : base;
    if (opt === correctAnswer) return `${base} ${styles.Quiz__option_correct}`;
    if (opt === selectedOption) return `${base} ${styles.Quiz__option_wrong}`;
    return `${base} ${styles.Quiz__option_dimmed}`;
  };

  const badgeColour = difficulty ? difficultyColour(difficulty, difficulties.indexOf(difficulty)) : null;

  return (
    <div className={styles.Quiz}>
      <div className={styles.Quiz__header}>
        <span className={styles.Quiz__title}>{subtileLabel}</span>
        <div className={styles.Quiz__header_right}>
          {difficulty && badgeColour && (
            <span className={styles.Quiz__difficulty_badge} style={{ backgroundColor: badgeColour }}>
              {difficulty}
            </span>
          )}
          <span className={styles.Quiz__timer}>{formatTime(elapsedSeconds)}</span>
          <span className={styles.Quiz__progress}>{currentIndex + 1} / {questions.length}</span>
        </div>
      </div>

      <div className={styles.Quiz__question}>{currentQuestion.question}</div>

      {usesStave && staveReady && derived && (
        <div className={styles.Quiz__stave}>
          <StaveRenderer
            notes={derived.notes}
            clef={derived.clef}
            timeSignature={derived.timeSignature}
            keySignature={derived.keySignature}
            ties={derived.ties}
            slurs={derived.slurs}
            barBreaks={derived.barBreaks}
          />
        </div>
      )}

      <div className={styles.Quiz__options}>
        {options.map((opt, idx) => (
          <div
            key={idx}
            className={optionClass(opt)}
            onClick={() => { if (!validated) setSelectedOption(opt); }}
          >
            <span className={styles.Quiz__option_letter}>{idx + 1}.</span>
            {currentQuestion.type === "sequence" ? (
              <button
                type="button"
                className={styles.Quiz__option_play}
                onClick={(e) => { e.stopPropagation(); playRhythm(decodeRhythm(opt), derived?.tempo ?? 100, countInFor(derived?.timeSignature)); }}
              >
                <span className={styles.Quiz__option_play_icon}>&#9654;</span>
                Play rhythm
              </button>
            ) : (
              opt
            )}
          </div>
        ))}
      </div>

      <div className={styles.Quiz__footer}>
        <Button
          variant="contained"
          disabled={selectedOption === null}
          onClick={handleNext}
          sx={{ backgroundColor: "#3a8bac", "&:hover": { backgroundColor: "#195d7a" } }}
        >
          {isLast && validated ? "Finish" : isLast ? "Check" : validated ? "Next" : "Check"}
        </Button>
      </div>
    </div>
  );
};

export default Quiz;
